import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PBPUTILS } from "../../config/API";
import jsPDF from 'jspdf';
import SuccessPage from '../SuccessPage';

const StampPacks = () => {
  // Get current date and calculate last month
  const getDefaultDate = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed (0 = January, 11 = December)

    // Calculate last month and year
    let lastMonth = currentMonth - 1;
    let year = currentYear;

    // If current month is January (0), last month is December of previous year
    if (lastMonth < 0) {
      lastMonth = 11; // December
      year = currentYear - 1;
    }

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return {
      month: monthNames[lastMonth],
      year: year.toString()
    };
  };

  const defaultDate = getDefaultDate();

  // Month options for dropdown
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Year options (current year and previous few years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - 3 + i).toString());

  // Pack type options
  const packTypeOptions = ['20s', '25s'];

  // Get taxpayer ID and business name from localStorage
  const [taxpayerId, setTaxpayerId] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [loadingStartingCount, setLoadingStartingCount] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [signatureDataURL, setSignatureDataURL] = useState('');
  const [printedName, setPrintedName] = useState('');
  const [title, setTitle] = useState('');

  // State to track if firstMonthInventory is editable - only make it read-only when API returns non-zero value
  const [isFirstMonthInventoryEditable, setIsFirstMonthInventoryEditable] = useState(true);

  // State to track if the value was manually modified
  const [isFirstMonthInventoryManuallyModified, setIsFirstMonthInventoryManuallyModified] = useState(false);

  const [formData, setFormData] = useState({
    // Stamp Order Section
    distributor: '', // Will be set from localStorage
    month: defaultDate.month,
    year: defaultDate.year,
    expirationDate: '',
    invoiceNumber: '',
    dateSent: '',
    numberOfRolls: '',
    startRollNumber: '',
    endRollNumber: '',
    numberOfStamps: '',

    // Financial Section
    exciseTaxDuea: '',
    exciseTaxDueb: '',
    shippingAmountDuea: '',
    shippingAmountDueb: '',
    varianceNumberOfStamps: '',
    additionalExciseTax: '',
    totalReceived: '',
    exciseTaxReceived: '',
    shippingAmountReceived: '',
    dateReceived: '',

    // Inventory Section - Single pack type for all rows
    packType: '20s', // Single pack type for all inventory items
    firstMonthInventory: '',
    stampedDuringMonth: '',
    returnedPacks: '',
    shippedToTribe: '',
    destroyedPacks: '',
    adjustment: '',
    adjustmentExplanation: ''
  });

  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDate] = useState(new Date().toLocaleDateString());

  // Get taxpayer ID and business name from localStorage on component mount
  useEffect(() => {
    const storedTaxpayerId = localStorage.getItem("selectedTaxPayer");
    const storedBusinessName = localStorage.getItem("selectedBusinessName");
    const storedExpirationDate = localStorage.getItem("businessLicenseExpirationDate");

    if (storedTaxpayerId) {
      setTaxpayerId(storedTaxpayerId);
      console.log('Retrieved taxpayer ID from localStorage:', storedTaxpayerId);
    } else {
      console.warn('No taxpayer ID found in localStorage');
      // Fallback to default if not found
      setTaxpayerId('109');
    }

    if (storedBusinessName) {
      setBusinessName(storedBusinessName);
      // Update distributor info with the business name from localStorage
      setFormData(prev => ({
        ...prev,
        distributor: storedBusinessName
      }));
      console.log('Retrieved business name from localStorage:', storedBusinessName);
    } else {
      console.warn('No business name found in localStorage');
      // Fallback to default if not found
      setFormData(prev => ({
        ...prev,
        distributor: 'Black Canyon Distribution, LLC - PBPN License #41112-7777'
      }));
    }

    if (storedExpirationDate) {
      setFormData(prev => ({
        ...prev,
        expirationDate: storedExpirationDate
      }));
    }
  }, []);

  // Format month for API (convert to number with leading zero)
  const formatMonthForAPI = (monthName) => {
    const monthMap = {
      'January': '01', 'February': '02', 'March': '03', 'April': '04',
      'May': '05', 'June': '06', 'July': '07', 'August': '08',
      'September': '09', 'October': '10', 'November': '11', 'December': '12'
    };
    return monthMap[monthName] || '01';
  };

  // Fetch starting count from API for firstMonthInventory
  const fetchStartingCount = async (selectedPackType = null) => {
    // Don't fetch if taxpayer ID is not available
    if (!taxpayerId) {
      console.warn('Taxpayer ID not available, skipping API call');
      return;
    }

    try {
      setLoadingStartingCount(true);

      const formattedMonth = formatMonthForAPI(formData.month);
      // Use the passed packType or fallback to formData.packType
      const packTypeToUse = selectedPackType || formData.packType;

      // Updated API URL with dynamic month and year from form data
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&date=${formattedMonth}-${formData.year}&reporttype=Packs&action=getStartCount&perpack=${packTypeToUse}`;

      console.log('Fetching starting count from:', apiUrl);

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.text();
      const count = parseInt(data.trim(), 10);

      if (!isNaN(count)) {
        setFormData(prev => ({
          ...prev,
          firstMonthInventory: count.toString()
        }));
        // Only make it read-only if API returns a non-zero value AND it hasn't been manually modified
        if (count !== 0 && !isFirstMonthInventoryManuallyModified) {
          setIsFirstMonthInventoryEditable(false);
        } else {
          setIsFirstMonthInventoryEditable(true);
        }
        console.log('Successfully loaded starting count:', count);
      } else {
        console.warn('Invalid count received:', data);
        // Set to 0 if invalid response
        setFormData(prev => ({
          ...prev,
          firstMonthInventory: '0'
        }));
        setIsFirstMonthInventoryEditable(true);
      }
    } catch (error) {
      console.error('Error fetching starting count:', error);
      // Set to 0 on error
      setFormData(prev => ({
        ...prev,
        firstMonthInventory: '0'
      }));
      setIsFirstMonthInventoryEditable(true);
    } finally {
      setLoadingStartingCount(false);
    }
  };

  // Fetch starting count when month, year, or taxpayer ID changes
  useEffect(() => {
    if (taxpayerId) {
      fetchStartingCount();
    }
  }, [formData.month, formData.year, taxpayerId]);

  // Initialize canvas size after component mounts
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-container');
      if (container) {
        const width = container.clientWidth - 32;
        const height = 200;
        setCanvasSize({ width, height });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const nextValue =
        e.target.type === 'number' && value !== ''
          ? String(Math.max(0, Number(value)))
          : value;
      const updatedData = {
        ...prev,
        [name]: nextValue
      };

      // Auto-calculate additionalExciseTax when varianceNumberOfStamps changes
      if (name === 'varianceNumberOfStamps') {
        const variance = parseFloat(nextValue) || 0;
        updatedData.additionalExciseTax = (variance * 0.17).toFixed(2);
      }

      return updatedData;
    });

    // Mark firstMonthInventory as manually modified when user types in it
    if (name === 'firstMonthInventory') {
      setIsFirstMonthInventoryManuallyModified(true);
      // Always keep it editable if manually modified
      setIsFirstMonthInventoryEditable(true);
    }
  };

  const handleMonthChange = (e) => {
    setFormData(prev => ({
      ...prev,
      month: e.target.value
    }));
  };

  const handleYearChange = (e) => {
    setFormData(prev => ({
      ...prev,
      year: e.target.value
    }));
  };

  const handlePackTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      packType: value
    }));

    // Call fetchStartingCount with the selected pack type value
    if (taxpayerId) {
      fetchStartingCount(value);
    }
  };

  // Helper function to convert base64 data URL to File object
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  // Add this helper function to convert PDF to Blob
  const pdfToBlob = (pdf) => {
    const pdfOutput = pdf.output('blob');
    return pdfOutput;
  };

  // Generate PDF function with signature and proper field labels - accepts optional signature override (use when generating for send, since setState is async)
  const generatePDF = (signatureDataURLOverride) => {
    const pdf = new jsPDF();
    const signatureToUse = signatureDataURLOverride !== undefined ? signatureDataURLOverride : signatureDataURL;

    // Set font and colors
    pdf.setFont('helvetica');
    pdf.setTextColor(0, 0, 0); // #840028

    // Header Section
    pdf.setFontSize(16);
    pdf.text('Monthly Distributor Report', 105, 20, { align: 'center' });

    pdf.setFontSize(12);
    pdf.text('Prairie Band Potawatomi Tax Commission', 105, 30, { align: 'center' });
    pdf.text('16281 Q Road', 105, 36, { align: 'center' });
    pdf.text('Mayetta, KS 66509', 105, 42, { align: 'center' });

    pdf.setFontSize(10);
    pdf.text('Reports and Payment due on the 10th of each Month', 105, 50, { align: 'center' });

    // Distributor Information
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Distributor Information:', 20, 65);
    pdf.setFontSize(10);
    pdf.text(`Distributor: ${formData.distributor}`, 20, 72);
    pdf.text(`Month: ${formData.month} ${formData.year}`, 20, 78);
    pdf.text(`PBPN Tribal Distributor License Expiration Date: ${formData.expirationDate}`, 20, 84);

    if (taxpayerId) {
      pdf.text(`Taxpayer ID: ${taxpayerId}`, 20, 90);
    }
    // if (businessName) {
    //   pdf.text(`Business: ${businessName}`, 20, 96);
    // }

    // Pack Type Information
    pdf.text(`Pack Type: ${formData.packType} packs`, 20, 104);

    // Stamp Packs Section
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('PBPN/KS Compact Distributor Information - Packs of Cigarettes', 20, 116);

    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    let yPosition = 126;

    const formatAmount = (value) => {
      if (value === null || value === undefined) return '0';
      const s = String(value).replace(/,/g, '').trim();
      if (s === '') return '0';
      const num = Number(s);
      if (isNaN(num)) return String(value);
      if (s.includes('.')) {
        const decimals = s.split('.')[1].length;
        return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      }
      return num.toLocaleString();
    };

    // Stamp Packs Data with proper labels and line numbers
    const stampPacksData = [
      {
        lineNumber: '19.',
        ksLine: 'KS ST Line 1.',
        label: 'First of month physical inventory of PBPN and PBPN/KS STAMPED cigarette packs',
        value: formData.firstMonthInventory || '0'
      },
      {
        lineNumber: '20.',
        ksLine: 'KS ST Line 2.',
        label: 'Cigarette packs STAMPED with PBPN or PBPN/KS stamps during the month',
        value: formData.stampedDuringMonth || '0'
      },
      {
        lineNumber: '21.',
        ksLine: 'KS ST Line 3.',
        label: 'STAMPED cigarette packs returned from PBPN retailers or PBP Tribe/Nation',
        value: formData.returnedPacks || '0'
      },
      {
        lineNumber: '22.',
        ksLine: 'KS ST Line 4.',
        label: 'Total PBPN or PBPN/KS STAMPED packs available during the month (Lines 19 + 20 + 21)',
        value: totalStampedAvailable,
        isCalculated: true
      },
      {
        lineNumber: '23.',
        ksLine: 'KS ST Line 5.',
        label: 'PBPN/KS STAMPED cigarette packs shipped to PBP Tribe/Nation',
        value: formData.shippedToTribe || '0'
      },
      {
        lineNumber: '24.',
        ksLine: 'KS ST Line 6.',
        label: 'PBPN or PBPN/KS STAMPED cigarette packs destroyed or returned to manufacturer',
        value: formData.destroyedPacks || '0'
      },
      {
        lineNumber: '25.',
        ksLine: 'KS ST Line 7.',
        label: 'Adjustment',
        value: formData.adjustment || '0'
      },
      {
        lineNumber: '26.',
        ksLine: 'KS ST Line 8.',
        label: 'Total STAMPED Cigarettes removed from inventory (Lines 23 + 24 + 25)',
        value: totalRemovedFromInventory,
        isCalculated: true
      },
      {
        lineNumber: '27.',
        ksLine: 'KS ST Line 9.',
        label: 'End of month physical inventory of PBPN STAMPED cigarette packs (Line 22 - Line 26)',
        value: endMonthInventory,
        isCalculated: true
      }
    ];

    stampPacksData.forEach((item) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }

      // Line number and KS line
      pdf.text(item.lineNumber, 20, yPosition);
      pdf.text(item.ksLine, 35, yPosition);

      // Label
      const labelLines = pdf.splitTextToSize(item.label, 100);
      pdf.text(labelLines, 60, yPosition);

      // Value - make calculated values bold
      if (item.isCalculated) {
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
      }
      pdf.text(formatAmount(item.value), 180, yPosition, { align: 'right' });

      // Reset font and color
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(0, 0, 0);

      yPosition += Math.max(labelLines.length * 6, 8);

      // Add adjustment explanation if this is the adjustment row and explanation exists
      if (item.lineNumber === '25.' && formData.adjustmentExplanation) {
        const explanationLines = pdf.splitTextToSize(`Explanation: ${formData.adjustmentExplanation}`, 140);
        yPosition += 4;
        explanationLines.forEach(line => {
          pdf.text(line, 65, yPosition);
          yPosition += 6;
        });
        yPosition += 4;
      }

      // Add extra space after main sections
      if (['22.', '26.'].includes(item.lineNumber)) {
        yPosition += 4;
      }
    });

    // Add a new page for signature and certification
    if (yPosition > 180) {
      pdf.addPage();
      yPosition = 20;
    }

    // Signature Section
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Signature and Certification', 105, yPosition, { align: 'center' });
    yPosition += 15;

    // Certification Text
    pdf.setFontSize(10);
    pdf.text('I certify that I am a duly authorized owner, partner, director, officer or', 20, yPosition);
    yPosition += 6;
    pdf.text('representative of the licensee and that the statements made herein are true', 20, yPosition);
    yPosition += 6;
    pdf.text('and correct to the best of my knowledge.', 20, yPosition);
    yPosition += 15;

    // Add signature image if available
    if (signatureToUse) {
      try {
        // Add signature image to PDF
        pdf.addImage(signatureToUse, 'PNG', 20, yPosition, 80, 40);
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Signature:', 20, yPosition - 5);
        yPosition += 50;
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Signature: Provided during submission', 20, yPosition);
        yPosition += 10;
      }
    } else {
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Signature: Provided during submission', 20, yPosition);
      yPosition += 10;
    }

    // Printed Name and Title
    pdf.text(`Printed Name: ${printedName}`, 20, yPosition);
    yPosition += 6;
    pdf.text(`Title: ${title}`, 20, yPosition);
    yPosition += 8;

    // Date Signed
    pdf.text(`Date Signed: ${currentDate}`, 20, yPosition);
    yPosition += 15;




    return pdf;
  };

  // Function to handle PDF download from SuccessPage
  const handleDownloadPDF = () => {
    const pdf = generatePDF();
    const fileName = `StampPacks_Report_${formData.month}_${formData.year}_${formData.packType}_${taxpayerId || 'unknown'}.pdf`;
    pdf.save(fileName);
  };

  // Function to handle back to form
  const handleBackToForm = () => {
    setShowSuccessScreen(false);
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
      setSignatureDataURL('');
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    // Signature completion logic
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataURL = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(dataURL);
    }
  };

  // Helper function to convert data URL to blob
  const dataURLtoBlob = (dataURL) => {
    const byteString = atob(dataURL.split(',')[1]);
    const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the report.');
      return;
    }

    if (!printedName.trim()) {
      alert('Please enter your printed name.');
      return;
    }

    if (!title.trim()) {
      alert('Please enter your title.');
      return;
    }

    try {
      setSubmitting(true);

      // Get signature data FIRST and update state
      const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(signatureData);

      // Generate PDF with signature passed in (state update is async, so state would still be old here)
      const pdf = generatePDF(signatureData);
      const pdfBlob = pdfToBlob(pdf);
      const pdfFile = new File([pdfBlob], `StampPacks_Report_${formData.month}_${formData.year}_${formData.packType}_${taxpayerId || 'unknown'}.pdf`, {
        type: 'application/pdf'
      });

      // Store submission data for PDF generation
      setSubmissionData({
        formData,
        taxpayerId,
        businessName,
        printedName,
        title,
        signatureDataURL: signatureData,
        calculations: {
          totalStampedAvailable,
          totalRemovedFromInventory,
          endMonthInventory
        }
      });

      // Prepare form data for API
      const apiFormData = new FormData();

      // Add the required fields
      apiFormData.append('beginningInventory', formData.firstMonthInventory || '0');
      apiFormData.append('stampedDuringMonth', formData.stampedDuringMonth || '0');
      apiFormData.append('returnedPacks', formData.returnedPacks || '0');
      apiFormData.append('shippedToTribe', formData.shippedToTribe || '0');
      apiFormData.append('destroyedPacks', formData.destroyedPacks || '0');
      apiFormData.append('adjustment', formData.adjustment || '0');
      apiFormData.append('adjustmentExplanation', formData.adjustmentExplanation || '');
      apiFormData.append('reportMonth', formData.month);
      apiFormData.append('reportYear', formData.year);
      apiFormData.append('perpack', formData.packType);

      // Add printed name and title
      apiFormData.append('printedName', printedName);
      apiFormData.append('title', title);

      // Add signature as a file
      const signatureFile = dataURLtoFile(signatureData, 'signature.png');
      apiFormData.append('Signature', signatureFile);

      // Append PDF to FormData
      apiFormData.append('PDF', pdfFile);

      // Add taxpayer ID and other required parameters
      const taxpayerIdToUse = taxpayerId || '109';

      // Construct API URL with query parameters
      const apiUrl = `${PBPUTILS}?taxpayer=${taxpayerIdToUse}&reporttype=Packs&test=true`;



      // Make API call
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: apiFormData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      console.log('API Response:', responseText);

      // Try to parse the response as JSON
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        // If parsing fails, treat as plain text success
        console.warn('Response is not valid JSON, treating as success:', responseText);
        setShowSuccessScreen(true);
        return;
      }

      // Check the response status
      if (responseData.Status === 'Error') {
        // Show error message
        alert(responseData.Message || 'An error occurred while submitting the report.');
        return;
      } else if (responseData.Status === 'Success') {
        // Show success screen
        setShowSuccessScreen(true);
        return;
      } else {
        // Unexpected response format
        throw new Error('Unexpected response format from server');
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      alert(`Error submitting report: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate derived values (simplified for single input fields)
  const totalStampedAvailable = (
    parseInt(formData.firstMonthInventory || 0) +
    parseInt(formData.stampedDuringMonth || 0) +
    parseInt(formData.returnedPacks || 0)
  ).toString();

  const totalRemovedFromInventory = (
    parseInt(formData.shippedToTribe || 0) +
    parseInt(formData.destroyedPacks || 0) +
    parseInt(formData.adjustment || 0)
  ).toString();

  const endMonthInventory = (
    parseInt(totalStampedAvailable || 0) -
    parseInt(totalRemovedFromInventory || 0)
  ).toString();

  // Prepare report details for SuccessPage
  const reportDetails = {
    'Report Type': 'Stamp Packs Inventory Report',

    'Business': businessName,
    'Period': `${formData.month} ${formData.year}`,
    'Submitted': currentDate,



  };

  // If showing success screen, render the SuccessPage component
  if (showSuccessScreen) {
    return (
      <SuccessPage
        title="Stamp Packs Report Submitted Successfully!"
        subtitle={`Your stamp packs inventory report for ${formData.packType} packs has been successfully submitted.`}
        downloadButtonText="Download Stamp Packs Report PDF"
        backButtonText="Back to Home"
        onDownload={handleDownloadPDF}
        onBack={handleBackToForm}
        reportDetails={reportDetails}
        contactInfo={{
          phone: "(785) 966-3060",
          email: "taxcommission@pbpnation.org",
          department: "Prairie Band Potawatomi Tax Commission"
        }}
      >
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Important Notes:</h3>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• Keep this PDF for your records</li>
            <li>• Ensure all inventory details are accurately recorded</li>
            <li>• Contact the Tax Commission staff if you need to make any corrections</li>
          </ul>
        </div>
      </SuccessPage>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-white">
      {/* Header Section */}
      <div className="text-center mb-6 sm:mb-8 border-b-2 border-[#840028] pb-4 sm:pb-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#840028]">Monthly Distributor Report</h1>
        <div className="font-bold mb-2 text-[#840028] text-sm sm:text-base">
          <div>Prairie Band Potawatomi Tax Commission</div>
          <div>16281 Q Road</div>
          <div>Mayetta, KS 66509</div>
        </div>
        <div className="italic mb-2 text-[#840028] text-sm sm:text-base">
          Reports and Payment due on the 10th of each Month
        </div>
        {taxpayerId && (
          <div className="text-xs sm:text-sm text-gray-600 mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-4">
            <span>Taxpayer ID: <strong>{taxpayerId}</strong></span>
            {businessName && (
              <span>Business: <strong>{businessName}</strong></span>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Distribution Header */}
        <div className="border border-gray-300 p-3 sm:p-4 mb-4 sm:mb-6">
          {/* Distributor Row - At Top */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-3">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Distributor:</label>
            <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-80 text-sm sm:text-base">
              {formData.distributor}
            </div>
          </div>

          {/* Month and Year Row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 mb-3">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Month:</label>
            <select
              value={formData.month}
              onChange={(e) => handleInputChange({ target: { name: 'month', value: e.target.value } })}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-32 sm:mr-8 text-sm sm:text-base"
            >
              {monthOptions.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>

            <label className="font-bold sm:w-12 sm:mr-4 text-[#840028] text-sm sm:text-base">Year:</label>
            <select
              value={formData.year}
              onChange={(e) => handleInputChange({ target: { name: 'year', value: e.target.value } })}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-20 text-sm sm:text-base"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="hidden">
            <input
              type="text"
              value={formData.reportMonth}
              readOnly
            />
            <input
              type="text"
              value={formData.reportYear}
              readOnly
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">PBPN Tribal Distributor License Expiration Date:</label>
            <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-32 text-sm sm:text-base">
              {formData.expirationDate}
            </div>
          </div>
        </div>

        <hr className="my-6 sm:my-8 border-gray-300" />

        {/* Inventory Section */}
        <div className="mb-6 sm:mb-8">
          <h3 className="bg-gray-100 px-3 sm:px-4 py-2 border-l-4 border-[#840028] font-semibold mb-3 sm:mb-4 text-[#840028] text-sm sm:text-base">
            PBPN/KS Compact Distributor Information - Packs of Cigarettes
          </h3>

          {/* Single Pack Type Selection */}
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 border border-gray-300 rounded-lg bg-gray-50">
            <label className="font-bold text-[#840028] mb-2 block text-sm sm:text-base">
              Select Pack Type (applies to all inventory items):
            </label>
            <select
              value={formData.packType}
              onChange={(e) => handlePackTypeChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#840028] bg-white sm:min-w-32 text-sm sm:text-base"
            >
              {packTypeOptions.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <p className="text-xs sm:text-sm text-gray-600 mt-2">
              Selected pack type: <span className="font-semibold">{formData.packType}</span> - This applies to all inventory items below.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 min-w-[600px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 p-2 text-left font-semibold text-xs sm:text-sm">
                    Number of cigarettes per pack
                  </th>
                  <th className="border border-gray-300 p-2 text-left font-semibold text-xs sm:text-sm">KS ST Line #</th>
                  <th className="border border-gray-300 p-2 text-right font-semibold text-xs sm:text-sm"># of Packs</th>
                </tr>
              </thead>
              <tbody>
                {/* Row 19 */}
                <tr>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">
                    19. First of month physical inventory of PBPN and PBPN/KS STAMPED cigarette packs
                    <span className="text-red-600 font-semibold block mt-1 text-xs text-right">
                      (should equal line 27 of last month) or (State line 9)
                    </span>
                  </td>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">1.</td>
                  <td className="border border-gray-300 p-2">
                    <div className="relative">
                      <input
                        type="number"
                        name="firstMonthInventory"
                        value={formData.firstMonthInventory}
                        onChange={handleInputChange}
                        min="0"
                        className={`w-full px-2 py-1 text-right border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#840028] focus:border-[#840028] text-sm ${!isFirstMonthInventoryEditable ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                        readOnly={!isFirstMonthInventoryEditable}
                      />
                      {loadingStartingCount && (
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        </div>
                      )}
                    </div>
                    {loadingStartingCount && (
                      <span className="text-xs text-blue-600">Loading starting count...</span>
                    )}
                  </td>
                </tr>

                {/* Row 20 */}
                <tr>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">
                    20. Cigarette packs STAMPED with PBPN or PBPN/KS stamps during the month (Line 4)
                  </td>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">2.</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="number"
                      name="stampedDuringMonth"
                      value={formData.stampedDuringMonth}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-2 py-1 border text-right border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#840028] focus:border-[#840028] text-sm"
                    />
                  </td>
                </tr>

                {/* Row 21 */}
                <tr>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">
                    21. STAMPED cigarette packs returned from PBPN retailers or PBP Tribe/Nation
                  </td>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">3.</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="number"
                      name="returnedPacks"
                      value={formData.returnedPacks}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-2 py-1 border text-right border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#840028] focus:border-[#840028] text-sm"
                    />
                  </td>
                </tr>

                {/* Row 22 - Calculated Total */}
                <tr>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">
                    22. Total PBPN or PBPN/KS STAMPED packs available during the month (Lines 19 + 20 + 21)
                  </td>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">4.</td>
                  <td className="border border-gray-300 p-2 text-right pr-3 bg-gray-50 font-semibold text-[#840028] text-sm">
                    {totalStampedAvailable}
                  </td>
                </tr>

                {/* Row 23 */}
                <tr>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">
                    23. PBPN/KS STAMPED cigarette packs shipped to PBP Tribe/Nation (Line 4)
                  </td>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">5.</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="number"
                      name="shippedToTribe"
                      value={formData.shippedToTribe}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-2 py-1 border border-gray-300 text-right rounded focus:outline-none focus:ring-1 focus:ring-[#840028] focus:border-[#840028] text-sm"
                    />
                  </td>
                </tr>

                {/* Row 24 */}
                <tr>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">
                    24. PBPN or PBPN/KS STAMPED cigarette packs destroyed or returned to manufacturer
                  </td>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">6.</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="number"
                      name="destroyedPacks"
                      value={formData.destroyedPacks}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-2 py-1 border text-right border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#840028] focus:border-[#840028] text-sm"
                    />
                  </td>
                </tr>

                {/* Row 25 */}
                <tr>
                  <td className="border border-gray-300 p-2">
                    <div className="flex flex-col space-y-2">
                      <span className="text-xs sm:text-sm">25. Adjustment (Please explain).</span>
                      <input
                        type="text"
                        name="adjustmentExplanation"
                        value={formData.adjustmentExplanation}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#840028] focus:border-[#840028] text-sm"
                        placeholder="Explanation"
                      />
                    </div>
                  </td>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">7.</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="number"
                      name="adjustment"
                      value={formData.adjustment}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-2 py-1 border text-right border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#840028] focus:border-[#840028] text-sm"
                    />
                  </td>
                </tr>

                {/* Row 26 - Calculated Total Removed */}
                <tr>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">
                    26. Total STAMPED Cigarettes removed from inventory (Lines 23 + 24 + 25)
                  </td>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">8.</td>
                  <td className="border border-gray-300 p-2 bg-gray-50 font-semibold text-right pr-3 text-[#840028] text-sm">
                    {totalRemovedFromInventory}
                  </td>
                </tr>

                {/* Row 27 - Calculated End Month Inventory */}
                <tr>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">
                    27. End of month physical inventory of PBPN STAMPED cigarette packs (Line 22 - Line 26)
                  </td>
                  <td className="border border-gray-300 p-2 text-xs sm:text-sm">9.</td>
                  <td className="border border-gray-300 p-2 bg-gray-50 text-right pr-3 font-semibold text-[#840028] text-sm">
                    {endMonthInventory}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Signature Section */}
        <div className="border border-gray-300 p-4 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-lg font-bold mb-3 sm:mb-4 text-center text-[#840028]">Signature</h3>

          <div className="mb-4">
            <label className="font-bold block mb-2 text-[#840028] text-sm sm:text-base">
              Sign below to certify the accuracy of this report:
            </label>
            <div
              id="signature-container"
              className="border-2 border-gray-300 rounded-lg p-3 sm:p-4 bg-white"
            >
              {canvasSize.width > 0 && (
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="#0000FF"
                  backgroundColor="rgb(255, 255, 255)"
                  canvasProps={{
                    width: canvasSize.width,
                    height: canvasSize.height,
                    className: 'signature-canvas w-full border border-gray-400 rounded bg-white cursor-crosshair'
                  }}
                  onBegin={handleSignatureBegin}
                  onEnd={handleSignatureEnd}
                  clearOnResize={false}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <div className="text-sm text-gray-600 text-center sm:text-left">
              {isSigned ? (
                <span className="text-green-600 font-bold">✓ Signature provided</span>
              ) : (
                <span className="text-red-600">Please sign above</span>
              )}
            </div>

            <button
              type="button"
              onClick={clearSignature}
              className="px-4 py-2 bg-[#840028] text-white rounded hover:bg-[#6a0020] transition duration-200 text-sm w-full sm:w-auto"
            >
              Clear Signature
            </button>
          </div>

          {/* Printed Name and Title Fields - Added */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Printed Name: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={printedName}
                  onChange={(e) => setPrintedName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Enter your full name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Enter your title"
                  required
                />
              </div>
            </div>

            {/* Date Signed Field */}
            <div>
              <label className="block text-sm font-medium text-[#840028] mb-2">
                Date Signed:
              </label>
              <input
                type="text"
                value={currentDate}
                readOnly
                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-medium text-gray-700 cursor-not-allowed text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                This date is automatically set to today's date and cannot be modified
              </p>
            </div>
          </div>
        </div>

        {/* Certification */}
        <div className="border-t-2 border-b-2 border-[#840028] py-4 sm:py-6 my-6 sm:my-8 text-center italic text-[#840028] text-sm sm:text-base">
          <p>
            I certify that I am a duly authorized owner, partner, director, officer or representative of the licensee and that the statements made herein are true and correct to the best of my knowledge.
          </p>
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={!isSigned || submitting || !printedName.trim() || !title.trim()}
            className="bg-[#840028] hover:bg-[#6a0020] text-white font-bold py-3 px-6 sm:px-8 rounded-lg transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto text-sm sm:text-base"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : (
              isSigned && printedName.trim() && title.trim() ? 'Submit Report' : 'Please Complete All Required Fields'
            )}
          </button>
        </div>
      </form>

      {/* Custom CSS for signature canvas */}
      <style jsx>{`
        .signature-canvas {
          cursor: crosshair !important;
          touch-action: none;
        }
        .signature-canvas:active {
          cursor: grabbing !important;
        }
        #signature-container {
          min-height: 200px;
        }
        
        @media (max-width: 640px) {
          #signature-container {
            min-height: 150px;
          }
        }
      `}</style>
    </div>
  );
};

export default StampPacks;